import { getDate } from '../../utils/date';

class DataToBatch {
  constructor (data = {}) {
    this.constituent = data.constituent;
    this.material = data.material;
    this.materialDetails = data.materialDetails;
    this.mft = data.mft;
    this.unconvertedTarget = data.unconvertedTarget;
    this.error = data.error;
    this.isDischarged = data.isDischarged || false;
    this.isFeedInterrupted = data.isFeedInterrupted || false;
  }
}

export class WeighedDataToBatch extends DataToBatch {
  constructor (data = {}) {
    super(data);
    this.bin = data.bin;
    this.binForDecum = data.binForDecum;
    this.binPercent = data.binPercent || {};
    this.scale = data.scale;
    this.isFeedFinish = data.isFeedFinish || false;
    this.jogSecondary = data.jogSecondary || false;
    // Substitution data
    this.isSubstituted = data.isSubstituted || false;
    this.substitutionWeight = data.substitutionWeight || 0;
    this.originalMaterial = data.originalMaterial || null;
    this.originalBin = data.originalBin || null;
    this.substitutionReason = data.substitutionReason || '';
    this.substitutionTimestamp = data.substitutionTimestamp || Date.now();
    //
    this.jogCounter = data.jogCounter || 0;
    this.targetDeduction = data.targetDeduction || 0;
    this.lastWeight = data.lastWeight || 0;
    this.autoJogOpenTime = data.autoJogOpenTime || 0;
    this.autoJogCounter = data.autoJogCounter || 0;
    this.feedLogs = [];
    this.startWeight = data.startWeight || 0;
    this.isTargetAdjusted = data.isTargetAdjusted || false;
    this.dynamicJog = data.dynamicJog || null;
    this.dynamicJogOpenTimes = data.dynamicJogOpenTimes || [];
    this.overcapacityTarget = data.overcapacityTarget || 0;
    this.unconvertedOvercapacityTarget = data.unconvertedOvercapacityTarget || 0;
    this.isFeedFinishHeadWater = data.isFeedFinishHeadWater || false;
    this.isFeedFinishWashWater = data.isFeedFinishWashWater || false;
    this.isDischargeFinishHeadWater = data.isDischargeFinishHeadWater || false;

    this.isFeedFinishProportionOne = data.isFeedFinishProportionOne || false;
    this.isFeedFinishProportionTwo = data.isFeedFinishProportionTwo || false;
    this.proportionOneTarget = data.proportionOneTarget || 0;
    this.proportionTwoTarget = data.proportionTwoTarget || 0;
    this.proportionOneActual = data.proportionOneActual || 0;
    this.proportionTwoActual = data.proportionTwoActual || 0;
    this.unconvertedProportionOneTarget = data.unconvertedProportionOneTarget || 0;
    this.unconvertedProportionTwoTarget = data.unconvertedProportionTwoTarget || 0;
    this.wasPrevDataMovedWithProportion = data.wasPrevDataMovedWithProportion || false;
    this.extraWater = data.extraWater || 0;
    this.extraWaterStarted = data.extraWaterStarted || false;
    this.extraWaterActual = data.extraWaterActual || 0;
  }
}

/**
 * Add feed log
 * @param {FeedLog || {}} data
 * @param {WeighedDataToBatch} weighedData
 */
export const addFeedLog = (data, weighedData) => {
  const { material, materialDetails, bin, startWeight, feedLogs = [] } = weighedData;
  const primaryBin = materialDetails.bins.find(v => v.isPrimary) || {};
  const secondaryBin = materialDetails.bins.find(v => !v.isPrimary && v.isActive) || {};
  const primaryBinId = primaryBin.binId;
  const secondaryBinId = secondaryBin.binId;
  const lastData = weighedData.feedLogs.length
    ? weighedData.feedLogs[weighedData.feedLogs.length-1]
    : null;
  const lastWeight = lastData ? lastData.scaleWeight || startWeight : startWeight;
  const diffWeight = data.scaleWeight - (lastWeight || weighedData.feedLogs.length < 3
    ? lastWeight
    : data.scaleWeight);

  weighedData.feedLogs.push(
    new FeedLog({
      materialName: material.batchCode || material.code,
      materialDetailId: materialDetails.id,
      operationOrder: feedLogs.length + 1,
      unitSymbol: materialDetails.itemBatchUnitSymbol,
      inventoryUnitSymbol: materialDetails.itemInventoryUnitSymbol,
      primaryBinId,
      secondaryBinId,
      primaryBinInventory: (materialDetails.deviceBalance
        .find(v => v.deviceId === primaryBinId) || {}).currentBalance,
      secondaryBinInventory: secondaryBinId
        ? (materialDetails.deviceBalance
          .find(v => v.deviceId === secondaryBinId) || {}).currentBalance
        : null,
      moisture: materialDetails.autoMoisture
        ? materialDetails.autoMoistureAmount
        : materialDetails.moistureAmount,
      accuracy: data.recommendation ? (diffWeight / data.recommendation) * 100 : null,
      weight: data.scaleWeight - startWeight,
      diffWeight,
      diffTime: lastData ? (Date.now() - lastData.timestamp)/1000 : 0,
      isAutoJog: bin ? bin.autoJog : false,
      isAutoTuning: bin ? bin.autoTuning : false,
      isDynamicFreeFall: bin ? bin.dynamicFreeFall : false,
      ...data,
    })
  );
};

export class FeedLog {
  constructor(data = {}) {
    this.batchTicketId = data.batchTicketId;
    this.materialName = data.materialName;
    this.materialDetailId = data.materialDetailId;
    this.operationOrder = data.operationOrder;
    this.operationName = data.operationName;
    this.startTime = getDate(Date.now(), 'YYYY-MM-DDTHH:mm:ss.SSS');
    this.timestamp = Date.now();
    this.duration = data.duration || 0;
    this.diffTime = data.diffTime || 0;
    this.weight = data.weight || 0;
    this.scaleWeight = data.scaleWeight || 0;
    this.unitSymbol = data.unitSymbol;
    this.diffWeight = data.diffWeight || 0;
    this.recommendation = data.recommendation;
    this.accuracy = data.accuracy;
    this.isAutoJog = data.isAutoJog || false;
    this.isAutoTuning = data.isAutoTuning || false;
    this.isDynamicFreeFall = data.isDynamicFreeFall || false;
    this.primaryBinId = data.primaryBinId;
    this.secondaryBinId = data.secondaryBinId;
    this.primaryBinInventory = data.primaryBinInventory || 0;
    this.secondaryBinInventory = data.secondaryBinInventory || 0;
    this.inventoryUnitSymbol = data.inventoryUnitSymbol;
    this.moisture = data.moisture || 0;
    this.temperature = data.temperature;
    this.humidity = data.humidity;
    this.pressure = data.pressure;
  }
}

export class MeteredDataToBatch extends DataToBatch {
  constructor (data = {}) {
    super(data);
    this.pump = data.pump;
    this.initialWaterValue = data.initialWaterValue || 0;
    this.initialValue = data.initialValue || null;
    this.headWater = data.headWater || 0;
    this.washWater = data.washWater || 0;
    this.headWaterActual = data.headWaterActual || 0;
    this.washWaterActual = data.washWaterActual || 0;
    this.headWaterStarted = data.headWaterStarted || false;
    this.headWaterStartedManually = data.headWaterStartedManually || false;
    this.headWaterDischarged = data.headWaterDischarged || false;
    this.washWaterDischarged = data.washWaterDischarged || false;
    this.washWaterReadyForDischarge = data.washWaterReadyForDischarge || false;
    this.precisionLossCalculated = data.precisionLossCalculated || false;
    this.targetPrecisionLossAdjustment = data.targetPrecisionLossAdjustment || 0;
    this.extraWater = data.extraWater || 0;
    this.extraWaterStarted = data.extraWaterStarted || false;
    this.extraWaterActual = data.extraWaterActual || 0;
  }
}
